import { nanoid } from '@reduxjs/toolkit'

import type { RenderedImageEdit } from '@buffer-mono/popcorn'
import type {
  BufferUpload,
  ImageUpload,
  LocalFile,
  Uploader,
  Upload as UploadEntity,
} from '@buffer-mono/uploader'
import { Upload, UploadSource } from '@buffer-mono/uploader'

interface UploadEditedImageOptions {
  uploader: Pick<Uploader, 'addFiles' | 'off' | 'on' | 'removeUpload'>
  renderedImage: RenderedImageEdit
  signal?: AbortSignal
}

interface EditedImageFile {
  extension: string
  mimeType: string
}

function getEditedImageFileType(mimeType: string): EditedImageFile {
  switch (mimeType.toLowerCase()) {
    case 'image/jpeg':
    case 'image/jpg':
      return { extension: 'jpg', mimeType: 'image/jpeg' }
    case 'image/png':
      return { extension: 'png', mimeType: 'image/png' }
    case 'image/webp':
      return { extension: 'webp', mimeType: 'image/webp' }
    default:
      throw new Error(`Unsupported edited image format: ${mimeType}`)
  }
}

function createAbortError(): DOMException {
  return new DOMException('Image upload was cancelled', 'AbortError')
}

export function createEditedImageFile(renderedImage: RenderedImageEdit): File {
  const fileType = getEditedImageFileType(renderedImage.mimeType)
  return new File([renderedImage.blob], `edited-image.${fileType.extension}`, {
    type: fileType.mimeType,
  })
}

export function uploadEditedImage({
  uploader,
  renderedImage,
  signal,
}: UploadEditedImageOptions): Promise<ImageUpload> {
  if (signal?.aborted) return Promise.reject(createAbortError())

  let editedFile: File
  try {
    editedFile = createEditedImageFile(renderedImage)
  } catch (error) {
    return Promise.reject(error)
  }

  const sessionId = nanoid()
  const source = UploadSource.imageEditor(sessionId)

  return new Promise<ImageUpload>((resolve, reject) => {
    let settled = false
    let startedUpload: UploadEntity | null = null

    function isMatchingSource(uploadSource: unknown): boolean {
      return (
        UploadSource.isImageEditor(uploadSource) &&
        uploadSource.id === sessionId
      )
    }

    function cleanup(): void {
      uploader.off('upload-finished', handleUploadFinished)
      uploader.off('upload-failed', handleUploadFailed)
      uploader.off('file-validation-failed', handleValidationFailed)
      uploader.off('upload-prevented', handleUploadPrevented)
      signal?.removeEventListener('abort', handleAbort)
    }

    function settleWithError(error: Error): void {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }

    function handleUploadFinished(upload: BufferUpload): void {
      if (!isMatchingSource(upload.meta.source)) return

      if (!Upload.isImage(upload)) {
        settleWithError(
          new Error('Edited image upload returned non-image media'),
        )
        return
      }

      if (settled) return
      settled = true
      cleanup()
      resolve(upload)
    }

    function handleUploadStarted(upload: UploadEntity): void {
      if (!isMatchingSource(upload.meta.source)) return

      startedUpload = upload
      if (signal?.aborted || settled) uploader.removeUpload(upload)
    }

    function handleUploadFailed(upload: UploadEntity, error: Error): void {
      if (isMatchingSource(upload.meta.source)) settleWithError(error)
    }

    function handleValidationFailed(
      invalidFile: LocalFile,
      error: Error,
    ): void {
      if (isMatchingSource(invalidFile.meta.source)) settleWithError(error)
    }

    function handleUploadPrevented(
      error: Error,
      files: LocalFile[] = [],
    ): void {
      if (files.some((candidate) => isMatchingSource(candidate.meta.source))) {
        settleWithError(error)
      }
    }

    function handleAbort(): void {
      if (startedUpload) uploader.removeUpload(startedUpload)
      settleWithError(createAbortError())
    }

    uploader.on('upload-started', handleUploadStarted)
    uploader.on('upload-finished', handleUploadFinished)
    uploader.on('upload-failed', handleUploadFailed)
    uploader.on('file-validation-failed', handleValidationFailed)
    uploader.on('upload-prevented', handleUploadPrevented)
    signal?.addEventListener('abort', handleAbort, { once: true })

    void uploader
      .addFiles([editedFile], { source, isReplacement: true })
      .catch((error: unknown) => {
        settleWithError(
          error instanceof Error
            ? error
            : new Error('Edited image upload could not be started'),
        )
      })
      .finally(() => uploader.off('upload-started', handleUploadStarted))
  })
}
